import InfoOutlined from "@mui/icons-material/InfoOutlined";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type ConditionalBranch,
  type ConditionExpr,
  EMPTY_CONDITIONAL_BRANCHES,
  formatConditionalTemplate,
  parseConditionalTemplate,
} from "./conditional";
import {
  CONDITIONAL_INLINE_ATTR,
  CONDITIONAL_TEMPLATE_ATTR,
  isDidReference,
} from "./constants";
import type { DynamicNodeOption } from "./dynamic-node-context";
import { useDynamicNodeContext } from "./dynamic-node-context";
import { DynamicNodePopover } from "./dynamic-node-popover";
import {
  DYNAMIC_NODE_EDIT_EVENT,
  hasNativeDynamicNodeTextSelection,
  isEventFromCurrentTarget,
} from "./dynamic-node-selection";
import "./dynamic-nodes.css";
import { renderMarkdownToHtml } from "./render-markdown";
import { collectReferencedKeys } from "./resolution";
import {
  consumeEditorOpen,
  getSlashDataKeyItems,
} from "./slash-command/data-key-registry";

type ChipState = "empty" | "loading" | "unresolved" | "resolved";

type ConditionSource = "context" | "dataKey";

/**
 * Editor for a single branch's condition. The variable is chosen with two
 * dropdowns — first the source (a context variable vs. one of the user's data
 * keys), then the variable itself from that source's list — followed by the
 * optional equality check (`is true` / `equals` / `not equal` + value). Both
 * sources resolve against keys the backend actually returns, so the condition
 * evaluates live.
 */
export const ConditionEditor: React.FC<{
  condition: ConditionExpr;
  contextOptions: DynamicNodeOption[];
  dataKeyOptions: DynamicNodeOption[];
  onChange: (condition: ConditionExpr) => void;
}> = ({ condition, contextOptions, dataKeyOptions, onChange }) => {
  const variable = condition.kind === "raw" ? "" : condition.variable;

  // Document identifiers belong to the Document Reference node, not here.
  const conditionContextOptions = useMemo(
    () => contextOptions.filter((option) => !isDidReference(option.key)),
    [contextOptions]
  );

  // The source dropdown is inferred from which list owns the current variable
  // (data keys win on a tie), defaulting to context for an unset variable. We
  // hold it in local state so switching the source for a not-yet-chosen variable
  // sticks.
  const [source, setSource] = useState<ConditionSource>(() =>
    dataKeyOptions.some((o) => o.key === variable)
      ? "dataKey"
      : conditionContextOptions.some((o) => o.key === variable)
        ? "context"
        : "context"
  );

  if (condition.kind === "raw") {
    return (
      <input
        className="tiptap-conditional-branch__raw"
        value={condition.expression}
        placeholder="handlebars expression"
        onChange={(event) =>
          onChange({ kind: "raw", expression: event.target.value })
        }
      />
    );
  }

  // The operator dropdown collapses the three `var`/`compare` shapes into one
  // control: "" = is true, "not" = is false, "eq"/"ne" = compare against a value.
  const op =
    condition.kind === "compare" ? condition.op : condition.negate ? "not" : "";
  const value = condition.kind === "compare" ? condition.value : "";

  const options =
    source === "dataKey" ? dataKeyOptions : conditionContextOptions;

  const withOp = (variableValue: string, opValue: string): ConditionExpr => {
    if (opValue === "eq" || opValue === "ne") {
      return { kind: "compare", variable: variableValue, op: opValue, value };
    }
    if (opValue === "not") {
      return { kind: "var", variable: variableValue, negate: true };
    }
    return { kind: "var", variable: variableValue };
  };

  const setVariable = (next: string): void => onChange(withOp(next, op));
  const setOp = (next: string): void => onChange(withOp(variable, next));

  return (
    <div className="tiptap-conditional-branch__condition">
      <select
        className="tiptap-conditional-branch__source"
        value={source}
        onChange={(event) => setSource(event.target.value as ConditionSource)}
      >
        <option value="context">Context</option>
        <option value="dataKey">Data key</option>
      </select>
      <select
        className="tiptap-conditional-branch__variable"
        value={options.some((o) => o.key === variable) ? variable : ""}
        onChange={(event) => setVariable(event.target.value)}
      >
        <option value="" disabled hidden />
        {options.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label ?? option.key}
          </option>
        ))}
      </select>
      <select
        className="tiptap-conditional-branch__op"
        value={op}
        onChange={(event) => setOp(event.target.value)}
      >
        <option value="">is true or exists</option>
        <option value="not">is false, missing, or 0</option>
        <option value="eq">equals</option>
        <option value="ne">not equal</option>
      </select>
      {op === "eq" || op === "ne" ? (
        <input
          className="tiptap-conditional-branch__value"
          value={value}
          placeholder="value"
          onChange={(event) =>
            onChange({
              kind: "compare",
              variable,
              op: op as "eq" | "ne",
              value: event.target.value,
            })
          }
        />
      ) : null}
    </div>
  );
};

/**
 * Conditional node: an inline chip for an `{{#if}}…{{/if}}` block. In the editable
 * editor it shows the live-evaluated active branch (green when its condition's
 * keys resolve, red when chosen-but-unresolved, yellow while loading/unset) and
 * opens a branch editor on click. When no branch is active it stays hidden unless
 * the editor-level show-all setting is enabled.
 */
export const ConditionalNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  deleteNode,
  editor,
  getPos,
}) => {
  const template = (node.attrs[CONDITIONAL_TEMPLATE_ATTR] as string) ?? "";
  const inline = node.attrs[CONDITIONAL_INLINE_ATTR] !== false;
  const {
    evaluateHandlebars,
    resolveDataKey,
    resolveContext,
    contextKeys,
    frozen,
    registerKeys,
    unregisterKeys,
    isKeyPending,
  } = useDynamicNodeContext();
  const nodeId = useId();
  const [interactive, setInteractive] = useState(() => editor.isEditable);
  const selectNode = useCallback(() => {
    const position = getPos();
    if (typeof position === "number") {
      editor.chain().focus().setNodeSelection(position).run();
    }
  }, [editor, getPos]);
  const position = getPos();
  const resolvedPosition =
    typeof position === "number" ? editor.state.doc.resolve(position) : null;
  const conditionalOnlyListItem =
    resolvedPosition !== null &&
    resolvedPosition.depth > 0 &&
    resolvedPosition.parent.type.name === "paragraph" &&
    resolvedPosition.parent.childCount === 1 &&
    resolvedPosition.node(resolvedPosition.depth - 1).type.name ===
      "listItem" &&
    resolvedPosition.node(resolvedPosition.depth - 1).childCount === 1;

  useEffect(() => {
    const updateInteractive = (): void => {
      setInteractive(editor.isEditable);
    };

    updateInteractive();
    editor.on("update", updateInteractive);
    return () => {
      editor.off("update", updateInteractive);
    };
  }, [editor]);

  const referencedKeys = useMemo(
    () => collectReferencedKeys(template),
    [template]
  );

  // Pull the keys this conditional depends on into the provider's batched request.
  useEffect(() => {
    registerKeys(nodeId, referencedKeys);
    return () => unregisterKeys(nodeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const rendered = evaluateHandlebars(template);

  // Resolution state for the chip colour: resolved (green) when a referenced key
  // has a value, unresolved (red) when keys are chosen but none resolve, loading
  // (yellow) while a fetch is in flight, empty (yellow) before a condition is set.
  const isResolved = (key: string): boolean =>
    !resolveDataKey(key).isUnresolved || !resolveContext(key).isUnresolved;
  const anyResolved = referencedKeys.some(isResolved);
  const anyPending = referencedKeys.some((key) => isKeyPending(key));
  const state: ChipState =
    referencedKeys.length === 0
      ? "empty"
      : anyResolved
        ? "resolved"
        : anyPending
          ? "loading"
          : "unresolved";

  return (
    <ConditionalChip
      template={template}
      inline={inline}
      conditionalOnlyListItem={conditionalOnlyListItem}
      rendered={rendered}
      state={state}
      frozen={frozen}
      contextKeys={contextKeys}
      interactive={interactive}
      onSelect={selectNode}
      onTemplateChange={(next) =>
        updateAttributes({ [CONDITIONAL_TEMPLATE_ATTR]: next })
      }
      onRemove={deleteNode}
    />
  );
};

/** Conditional chip; editable instances can open the branch-editor bubble. */
const ConditionalChip: React.FC<{
  template: string;
  inline: boolean;
  conditionalOnlyListItem: boolean;
  rendered: string;
  state: ChipState;
  frozen: boolean;
  contextKeys: DynamicNodeOption[];
  interactive: boolean;
  onSelect: () => void;
  onTemplateChange: (template: string) => void;
  onRemove: () => void;
}> = ({
  template,
  inline,
  conditionalOnlyListItem,
  rendered,
  state,
  frozen,
  contextKeys,
  interactive,
  onSelect,
  onTemplateChange,
  onRemove,
}) => {
  const [editing, setEditing] = useState(false);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // The colour legend in the popover is hidden by default; the "i" toggle in the
  // header reveals it. (The chip's status colours always show.)
  const [showInfo, setShowInfo] = useState(false);
  const { showAllConditionals, onShowAllConditionalsChange } =
    useDynamicNodeContext();

  // A brand-new (empty) conditional opens straight into the branch editor with a
  // single unset `if` branch, rather than the raw-template fallback.
  const parsed = useMemo(
    () =>
      template.trim() === ""
        ? { branches: EMPTY_CONDITIONAL_BRANCHES, parseable: true }
        : parseConditionalTemplate(template),
    [template]
  );

  // Auto-open the bubble for a freshly inserted (empty) conditional.
  useEffect(() => {
    if (interactive && template === "" && consumeEditorOpen("conditional")) {
      setEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!interactive) {
      return;
    }
    const element = wrapperRef.current;
    if (!element) {
      return;
    }
    const openEditor = () => setEditing(true);
    element.addEventListener(DYNAMIC_NODE_EDIT_EVENT, openEditor);
    return () =>
      element.removeEventListener(DYNAMIC_NODE_EDIT_EVENT, openEditor);
  }, [interactive]);

  useEffect(() => {
    if (editing && !showAllConditionals) {
      onShowAllConditionalsChange(true);
    }
  }, [editing, onShowAllConditionalsChange, showAllConditionals]);

  useEffect(() => {
    if (!editing) {
      return;
    }
    const onDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      // An in-popover action (adding/removing a branch) can remove the very
      // element that was clicked during its synchronous re-render — e.g. the
      // "+ Else" button disappears once an else exists, and the × removes its own
      // branch. By the time this fires the target is detached, so `contains` would
      // wrongly report "outside". Treat a now-detached target as an internal click
      // and keep the popover open.
      if (!target.isConnected) {
        return;
      }
      if (
        popoverRef.current?.contains(target) ||
        wrapperRef.current?.contains(target)
      ) {
        return;
      }
      setEditing(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editing]);

  // The condition's two dropdowns draw from separate sources: context variables
  // and the user's data keys. Data keys are listed by their key name (not the
  // question label) — drop the label so the dropdown falls back to the key.
  const dataKeyOptions = useMemo<DynamicNodeOption[]>(
    () => getSlashDataKeyItems().map((item) => ({ key: item.key })),
    [editing]
  );
  const handleChipClick = (event: React.MouseEvent): void => {
    if (!isEventFromCurrentTarget(event.currentTarget, event.target)) {
      return;
    }
    if (
      hasNativeDynamicNodeTextSelection(
        event.currentTarget,
        event.currentTarget.ownerDocument.getSelection()
      )
    ) {
      return;
    }
    onSelect();
  };

  // Write a mutated branch set back to the node as a formatted template.
  const commitBranches = (branches: ConditionalBranch[]): void => {
    onTemplateChange(formatConditionalTemplate(branches, { block: !inline }));
  };

  const branches = parsed.parseable
    ? parsed.branches
    : EMPTY_CONDITIONAL_BRANCHES;

  const updateBranch = (
    index: number,
    next: Partial<ConditionalBranch>
  ): void =>
    commitBranches(
      branches.map((branch, i) =>
        i === index ? { ...branch, ...next } : branch
      )
    );

  const removeBranch = (index: number): void =>
    commitBranches(branches.filter((_, i) => i !== index));

  const addElseIf = (): void => {
    const elseBranch = branches.find((b) => b.condition === null);
    const ifBranches = branches.filter((b) => b.condition !== null);
    const next: ConditionalBranch = {
      condition: { kind: "var", variable: "" },
      body: "",
    };
    commitBranches(
      elseBranch ? [...ifBranches, next, elseBranch] : [...branches, next]
    );
  };

  const addElse = (): void =>
    commitBranches([...branches, { condition: null, body: "" }]);

  const hasElse = branches.some((b) => b.condition === null);

  // Keep empty conditionals visible so authors can configure them. A configured
  // conditional with no evaluated output is false and follows the visibility
  // toggle.
  const isFalse = Boolean(template) && rendered.trim().length === 0;
  const shouldHideFalse = isFalse && !showAllConditionals;

  const chipClassName = [
    "tiptap-conditional",
    "tiptap-conditional--chip",
    `tiptap-conditional--${state}`,
    conditionalOnlyListItem ? "tiptap-conditional--list-item" : "",
    isFalse ? "tiptap-conditional--false" : "",
    shouldHideFalse ? "tiptap-conditional--hidden" : "",
    frozen ? "tiptap-conditional--frozen" : "",
    !interactive ? "tiptap-conditional--readonly" : "",
    editing ? "tiptap-conditional--editing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (shouldHideFalse) {
    return (
      <NodeViewWrapper
        as="span"
        ref={wrapperRef}
        className={chipClassName}
        contentEditable={false}
        aria-hidden="true"
      />
    );
  }

  const falseValue = branches.find(
    (branch) => branch.condition !== null && branch.body.trim().length > 0
  )?.body;
  const valueToDisplay = isFalse ? falseValue : rendered;
  const renderedMarkdown = valueToDisplay
    ? renderMarkdownToHtml(valueToDisplay)
    : null;

  return (
    <NodeViewWrapper
      as="span"
      ref={wrapperRef}
      className={chipClassName}
      contentEditable={false}
      data-dynamic-node-editor={interactive || undefined}
      title={template || "Empty conditional"}
      onClick={interactive ? handleChipClick : undefined}
    >
      {renderedMarkdown ? (
        <span
          className="tiptap-conditional__value"
          dangerouslySetInnerHTML={{ __html: renderedMarkdown.html }}
        />
      ) : (
        <span className="tiptap-conditional__value">
          {isFalse ? "False conditional" : "Empty conditional"}
        </span>
      )}

      {interactive && editing && wrapperRef.current ? (
        <DynamicNodePopover
          ref={popoverRef}
          anchorEl={wrapperRef.current}
          className="tiptap-conditional-popover"
        >
          <div className="tiptap-conditional-popover__header">
            <span>Conditional</span>
            <button
              type="button"
              className={
                showInfo
                  ? "tiptap-conditional-popover__info is-active"
                  : "tiptap-conditional-popover__info"
              }
              title="Show resolution status"
              aria-pressed={showInfo}
              onMouseDown={(event) => {
                event.preventDefault();
                setShowInfo((value) => !value);
              }}
            >
              <InfoOutlined fontSize="inherit" />
            </button>
          </div>
          {showInfo ? (
            <div className="tiptap-conditional-popover__legend">
              <span className="tiptap-conditional-popover__legend-item">
                <span className="tiptap-conditional-popover__swatch tiptap-conditional-popover__swatch--resolved" />
                conditional value resolved
              </span>
              <span className="tiptap-conditional-popover__legend-item">
                <span className="tiptap-conditional-popover__swatch tiptap-conditional-popover__swatch--loading" />
                conditional value loading
              </span>
              <span className="tiptap-conditional-popover__legend-item">
                <span className="tiptap-conditional-popover__swatch tiptap-conditional-popover__swatch--unresolved" />
                conditional value not found
              </span>
            </div>
          ) : null}

          {parsed.parseable ? (
            <>
              {branches.map((branch, index) => (
                <div key={index} className="tiptap-conditional-branch">
                  <div className="tiptap-conditional-branch__head">
                    <span className="tiptap-conditional-branch__keyword">
                      {branch.condition === null
                        ? "Else"
                        : index === 0
                          ? "If"
                          : "Else if"}
                    </span>
                    {branch.condition !== null ? (
                      <ConditionEditor
                        condition={branch.condition}
                        contextOptions={contextKeys}
                        dataKeyOptions={dataKeyOptions}
                        onChange={(condition) =>
                          updateBranch(index, { condition })
                        }
                      />
                    ) : null}
                    {branches.length > 1 ? (
                      <button
                        type="button"
                        className="tiptap-conditional-branch__remove"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          removeBranch(index);
                        }}
                        title="Remove branch"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                  <textarea
                    className="tiptap-conditional-branch__body"
                    value={branch.body}
                    placeholder="Content shown when this branch is taken"
                    onChange={(event) =>
                      updateBranch(index, { body: event.target.value })
                    }
                  />
                </div>
              ))}
            </>
          ) : (
            <textarea
              className="tiptap-conditional-branch__body tiptap-conditional-branch__body--raw"
              value={template}
              placeholder="{{#if …}}…{{/if}}"
              onChange={(event) => onTemplateChange(event.target.value)}
            />
          )}

          <div className="tiptap-conditional-popover__actions">
            {parsed.parseable ? (
              <>
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    addElseIf();
                  }}
                >
                  + Else if
                </button>
                {!hasElse ? (
                  <button
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      addElse();
                    }}
                  >
                    + Else
                  </button>
                ) : null}
              </>
            ) : null}
            <button
              type="button"
              className="tiptap-conditional-popover__remove"
              onMouseDown={(event) => {
                event.preventDefault();
                onRemove();
              }}
            >
              Remove conditional
            </button>
          </div>
        </DynamicNodePopover>
      ) : null}
    </NodeViewWrapper>
  );
};

export default ConditionalNodeView;
